import { Pencil, Trash, Plus, Copy, Eye, EyeOff } from 'lucide-react'
import React, { useEffect, useState, useRef } from 'react'
import { Toaster, toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'

// Import our new store hooks
import {
  LangflowConfig,
  useGetLangflowConfigs,
  useCreateLangflowConfig,
  useUpdateLangflowConfig,
  useDeleteLangflowConfig,
} from '~/stores/langflowStore'

interface ConfigItem {
  key: string
  value: string
}

const LangflowManager = () => {
  // Use store hooks
  const {
    configs,
    configsLoading,
    configsFetchSuccess,
    configsFetchError,
    configsErrorMessage,
    fetchConfigs,
  } = useGetLangflowConfigs((state) => ({
    configs: state.configs,
    configsLoading: state.configsLoading,
    configsFetchSuccess: state.configsFetchSuccess,
    configsFetchError: state.configsFetchError,
    configsErrorMessage: state.configsErrorMessage,
    fetchConfigs: state.fetchConfigs,
  }))

  // Create CRUD hooks from store
  const createConfigHook = useCreateLangflowConfig()
  const updateConfigHook = useUpdateLangflowConfig()
  const deleteConfigHook = useDeleteLangflowConfig()

  // Component state
  const [formVisible, setFormVisible] = useState<boolean>(false)
  const [editMode, setEditMode] = useState<boolean>(false)
  const [selectedConfig, setSelectedConfig] = useState<LangflowConfig | null>(
    null
  )
  const [instanceKey, setInstanceKey] = useState<string>('')
  const [name, setName] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [isActive, setIsActive] = useState<boolean>(true)
  const [apiKey, setApiKey] = useState<string>('')
  const [apiToken, setApiToken] = useState<string>('')
  const [configItems, setConfigItems] = useState<ConfigItem[]>([
    { key: '', value: '' },
  ])

  // State for password visibility
  const [showPassword, setShowPassword] = useState(false)
  const [showApiToken, setShowApiToken] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [configToDelete, setConfigToDelete] = useState<string | null>(null)

  // Fetch all configurations on component mount
  useEffect(() => {
    fetchConfigs()
  }, [fetchConfigs])

  // Show error toast when fetch fails
  useEffect(() => {
    if (configsFetchError) {
      toast.error(
        configsErrorMessage || 'Failed to load Langflow configurations'
      )
    }
  }, [configsFetchError, configsErrorMessage])

  const handleAddConfig = () => {
    setEditMode(false)
    setInstanceKey('')
    setName('')
    setDescription('')
    setIsActive(true)
    setApiKey('')
    setApiToken('')
    setConfigItems([{ key: '', value: '' }])
    setFormVisible(true)
  }

  const handleEditConfig = (config: LangflowConfig) => {
    setEditMode(true)
    setSelectedConfig(config)
    setInstanceKey(config.instanceKey)
    setName(config.name)
    setDescription(config.description || '')
    setIsActive(config.isActive)
    setApiKey(config.APIKey || '')
    // For security, we don't populate the actual token - user will need to re-enter if they want to change it
    setApiToken('')

    // Format configItems from object to array
    const items: ConfigItem[] = []
    if (config.configItems) {
      Object.entries(config.configItems).forEach(([key, value]) => {
        items.push({ key, value: String(value) })
      })
    }

    setConfigItems(items.length > 0 ? items : [{ key: '', value: '' }])
    setFormVisible(true)
  }

  const handleDeleteConfig = (configId: string) => {
    setConfigToDelete(configId)
    setDeleteDialogOpen(true)
  }

  const confirmDeleteConfig = () => {
    if (configToDelete) {
      deleteConfigHook.execute({
        id: configToDelete,
        onSuccess: () => {
          toast.success('Configuration deleted successfully')
          fetchConfigs()
        },
        onError: (error) => {
          console.error('Error deleting configuration:', error)
          toast.error('Failed to delete configuration')
        },
      })
      setDeleteDialogOpen(false)
      setConfigToDelete(null)
    }
  }

  const handleSubmit = () => {
    try {
      if (!instanceKey || !name) {
        toast.error('Instance key and name are required')
        return
      }

      // Convert configItems array to object
      const configItemsObj: Record<string, string> = {}
      configItems.forEach((item) => {
        if (item.key && item.value) {
          configItemsObj[item.key] = item.value
        }
      })

      const payload = {
        instanceKey,
        name,
        description,
        isActive,
        configItems: JSON.stringify(configItemsObj),
        APIKey: apiKey || undefined,
        APIToken: apiToken || undefined,
      }

      if (editMode && selectedConfig) {
        updateConfigHook.execute({
          id: selectedConfig.id,
          data: payload,
          onSuccess: () => {
            toast.success('Configuration updated successfully')
            setFormVisible(false)
            fetchConfigs()
          },
          onError: (error) => {
            console.error('Error updating configuration:', error)
            toast.error('Failed to update configuration')
          },
        })
      } else {
        createConfigHook.execute({
          data: payload,
          onSuccess: () => {
            toast.success('Configuration created successfully')
            setFormVisible(false)
            fetchConfigs()
          },
          onError: (error) => {
            console.error('Error creating configuration:', error)
            toast.error('Failed to create configuration')
          },
        })
      }
    } catch (error) {
      console.error('Error in form submission:', error)
      toast.error('An unexpected error occurred')
    }
  }

  const addConfigItem = () => {
    setConfigItems([...configItems, { key: '', value: '' }])
  }

  const updateConfigItem = (index: number, field: string, value: any) => {
    const updatedItems = [...configItems]
    updatedItems[index] = { ...updatedItems[index], [field]: value }
    setConfigItems(updatedItems)
  }

  const removeConfigItem = (index: number) => {
    if (configItems.length > 1) {
      const updatedItems = configItems.filter((_, i) => i !== index)
      setConfigItems(updatedItems)
    }
  }

  const formatDate = (value: string) => {
    return new Date(value).toLocaleString()
  }

  const configCountTemplate = (rowData: LangflowConfig) => {
    const count = Object.keys(rowData.configItems || {}).length
    return <span>{count}</span>
  }

  const activeTemplate = (rowData: LangflowConfig) => {
    return (
      <i
        className={`pi ${rowData.isActive ? 'pi-check-circle text-green-500' : 'pi-times-circle text-red-500'}`}
      ></i>
    )
  }

  const actionTemplate = (rowData: LangflowConfig) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-pencil"
          rounded
          outlined
          className="p-button-sm"
          onClick={() => handleEditConfig(rowData)}
        />
        <Button
          icon="pi pi-trash"
          rounded
          outlined
          severity="danger"
          className="p-button-sm"
          onClick={() => handleDeleteConfig(rowData)}
        />
      </div>
    )
  }

  const hasApiKeyTemplate = (rowData: LangflowConfig) => {
    return (
      <i
        className={`pi ${rowData.APIKey ? 'pi-check-circle text-green-500' : 'pi-times-circle text-red-500'}`}
      ></i>
    )
  }

  const hasApiTokenTemplate = (rowData: LangflowConfig) => {
    return (
      <i
        className={`pi ${rowData.APIToken ? 'pi-check-circle text-green-500' : 'pi-times-circle text-red-500'}`}
      ></i>
    )
  }

  const renderDialogFooter = () => {
    return (
      <div>
        <Button
          label="Cancel"
          icon="pi pi-times"
          outlined
          onClick={() => setFormVisible(false)}
          className="mr-2"
        />
        <Button
          label="Save"
          icon="pi pi-check"
          onClick={handleSubmit}
          loading={createConfigHook.loading || updateConfigHook.loading}
        />
      </div>
    )
  }

  return (
    <div className="langflow-manager">
      <Toaster />

      {/* AlertDialog for delete confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Configuration</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this configuration? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteConfig}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="flex justify-between items-center mb-4">
        <h2>Langflow Configuration Manager</h2>
        <Button onClick={handleAddConfig} className="flex items-center">
          <Plus className="h-4 w-4 mr-2" />
          Add Configuration
        </Button>
      </div>

      <div className="card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead style={{ width: '20%' }}>Name</TableHead>
              <TableHead>Instance Key</TableHead>
              <TableHead>Description</TableHead>
              <TableHead style={{ width: '10%' }}>Config Items</TableHead>
              <TableHead style={{ width: '8%' }}>API Key</TableHead>
              <TableHead style={{ width: '8%' }}>API Token</TableHead>
              <TableHead style={{ width: '8%' }}>Active</TableHead>
              <TableHead style={{ width: '15%' }}>Last Updated</TableHead>
              <TableHead style={{ width: '15%', textAlign: 'center' }}>
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(configs || []).map((config) => (
              <TableRow key={config.id}>
                <TableCell>{config.name}</TableCell>
                <TableCell>{config.instanceKey}</TableCell>
                <TableCell>{config.description}</TableCell>
                <TableCell>{config.configItems?.length || 0}</TableCell>
                <TableCell>{config.APIKey ? 'Yes' : 'No'}</TableCell>
                <TableCell>{config.APIToken ? 'Yes' : 'No'}</TableCell>
                <TableCell>{config.isActive ? 'Yes' : 'No'}</TableCell>
                <TableCell>{formatDate(config.updatedAt)}</TableCell>
                <TableCell>
                  <div className="flex justify-center space-x-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleEditConfig(config)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleDeleteConfig(config.id)}
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={formVisible} onOpenChange={setFormVisible}>
        <DialogContent className="sm:max-w-[70vw]">
          <DialogHeader>
            <DialogTitle>
              {editMode
                ? `Edit Configuration${selectedConfig ? ': ' + selectedConfig.name : ''}`
                : 'Add New Configuration'}
            </DialogTitle>
          </DialogHeader>

          <div className="p-fluid max-h-[60vh] overflow-auto">
            {/* Basic Information Section */}
            <div className="mb-4">
              <h4 className="mb-4 font-medium text-lg">Basic Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="instanceKey" className="font-medium">
                    Instance Key*
                  </Label>
                  <Input
                    id="instanceKey"
                    value={instanceKey}
                    onChange={(e) => setInstanceKey(e.target.value)}
                    disabled={editMode}
                    placeholder="e.g., langflow-1"
                    className={!instanceKey ? 'border-destructive' : ''}
                  />
                  {!instanceKey && (
                    <p className="text-sm text-destructive">
                      Instance key is required
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name" className="font-medium">
                    Name*
                  </Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., Production Langflow"
                    className={!name ? 'border-destructive' : ''}
                  />
                  {!name && (
                    <p className="text-sm text-destructive">Name is required</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description" className="font-medium">
                    Description
                  </Label>
                  <Textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    placeholder="Optional description of this configuration"
                  />
                </div>
              </div>
            </div>

            {/* API Settings Section */}
            <div className="mb-4">
              <h4 className="mb-4 font-medium text-lg">API Settings</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="apiKey" className="font-medium">
                    API Key
                  </Label>
                  <div className="relative">
                    <Input
                      id="apiKey"
                      type={showPassword ? 'text' : 'password'}
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      placeholder="Enter API Key (optional)"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                      <span className="sr-only">
                        {showPassword ? 'Hide password' : 'Show password'}
                      </span>
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="apiToken" className="font-medium">
                    API Token
                  </Label>
                  <div className="relative">
                    <Input
                      id="apiToken"
                      type={showApiToken ? 'text' : 'password'}
                      value={apiToken}
                      onChange={(e) => setApiToken(e.target.value)}
                      placeholder={
                        editMode
                          ? 'Leave empty to keep current token'
                          : 'Langflow API Token'
                      }
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full"
                      onClick={() => setShowApiToken(!showApiToken)}
                    >
                      {showApiToken ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                      <span className="sr-only">
                        {showApiToken ? 'Hide token' : 'Show token'}
                      </span>
                    </Button>
                  </div>
                  {editMode && (
                    <p className="text-sm text-muted-foreground">
                      Leave empty to keep current token
                    </p>
                  )}
                </div>
              </div>
            </div>

            <hr className="mb-4" />

            {/* Configuration Items Section */}
            <div className="mb-4">
              <div className="flex justify-between items-center mb-4">
                <h4 className="font-medium text-lg">Configuration Items</h4>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addConfigItem}
                  className="flex items-center"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Item
                </Button>
              </div>

              {configItems.map((item, i) => (
                <div key={i} className="mb-4 flex items-center space-x-2">
                  <div className="flex-grow">
                    <Input
                      value={item.key}
                      onChange={(e) =>
                        updateConfigItem(i, 'key', e.target.value)
                      }
                      placeholder="Key (e.g., DOCUMENT_NODE_URL)"
                    />
                  </div>
                  <div className="flex-grow">
                    <Input
                      value={item.value}
                      onChange={(e) =>
                        updateConfigItem(i, 'value', e.target.value)
                      }
                      placeholder="Value (e.g., http://127.0.0.1:6753/api/endpoint)"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => removeConfigItem(i)}
                    disabled={configItems.length <= 1}
                  >
                    <Trash className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setFormVisible(false)}
              className="mr-2"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={createConfigHook.loading || updateConfigHook.loading}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default LangflowManager
